import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

import { EvchargeTelemetryService } from '../../lib/services/evcharge-telemetry.service';
import { requestSystemEvchargeDailyTelemetry, setSystemEvchargeDailyTelemetryError, updateSystemEvchargeDailyTelemetry } from '../actions/block.actions';
import { AppState, getEmsConfig } from '..';

@Injectable()
export class EvchargeTelemetryEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly evchargeTelemetryService: EvchargeTelemetryService,
  ) {}

  requestSystemEvchargeDailyTelemetry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemEvchargeDailyTelemetry),
      tap(({ systemId, deviceId, range }) =>
        console.log(`🔄 EVCharge V2 Effect: requesting data for system ${systemId}, device ${deviceId}, range ${range.from} to ${range.to}`)
      ),
      debounceTime(300),
      withLatestFrom(this.store),
      switchMap(([{ systemId, instanceId, range, deviceId, forceRefresh }, {systemState}]) => {
        const system = systemState.systemList[systemId];
        if (!system) {
          throw new Error(`System with id ${systemId} not found`);
        }

        const emsConfig = getEmsConfig(system);

        if (!deviceId || !emsConfig) {
          console.warn('❌ EVCharge V2 Effect: Device ID is required');
          return of(setSystemEvchargeDailyTelemetryError({
            systemId,
            instanceId,
            deviceId: '',
            error: 'Device ID is required'
          }));
        }

        return this.evchargeTelemetryService.getEvChargeTelemetryData(
          systemId,
          deviceId,
          range.from,
          range.to,
          emsConfig.type,
          emsConfig?.loggerId,
        ).pipe(
          tap((telemetryData) => {
            const dataloggerType = telemetryData.length > 0 ?
              ((telemetryData[0] as any).__typename === 'AHEvChRT' ? 'AzzurroHub' : 'Connext') :
              'Unknown';
            console.log(`✅ EVCharge V2 Effect: received ${telemetryData.length} data points from ${dataloggerType}`);
          }),
          map((telemetryData) =>
            updateSystemEvchargeDailyTelemetry({
              systemId,
              instanceId,
              range,
              deviceId,
              telemetryData
            })
          ),
          catchError((error) => {
            console.error('❌ EVCharge V2 Effect error:', error);
            return of(setSystemEvchargeDailyTelemetryError({
              systemId,
              instanceId,
              deviceId,
              error: error.message || 'Failed to load EVCharge telemetry data'
            }));
          })
        );
      })
    )
  );
}
